import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { isGroupScopeRole } from '../../constants/csAdminRoles';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Stack,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../context/AuthContext';
import { csAdminApi } from '../../services/api';
import { LOOKUP_CATEGORIES } from '../../constants/lookupFallback';
import { clearLookupCache, fetchLookups } from '../../hooks/useLookups';
import { getDocumentTypeRules } from '../../utils/documentTypeFields';

const DOCUMENT_TYPE_CATEGORIES = new Set(['COMPANY_DOCUMENT_TYPE', 'DIRECTOR_DOCUMENT_TYPE']);

const isDocumentTypeCategory = (category) => DOCUMENT_TYPE_CATEGORIES.has(category);
const isCompanyTypeCategory = (category) => category === 'COMPANY_TYPE';

const emptyForm = () => ({
  code: '',
  label: '',
  sortOrder: 0,
  isActive: true,
  requiresExpiry: false,
  requiresIssueDate: false,
  requiresDocumentNumber: false,
  companyStructure: '',
});

const formFromRow = (row) => {
  const rules = getDocumentTypeRules(row);
  const meta = row.metadata || {};
  return {
    code: row.code,
    label: row.label,
    sortOrder: row.sortOrder || 0,
    isActive: row.isActive !== false,
    requiresExpiry: rules.requiresExpiry,
    requiresIssueDate: rules.requiresIssueDate,
    requiresDocumentNumber: rules.requiresDocumentNumber,
    companyStructure: meta.structure || '',
  };
};

const buildDocumentMetadata = (form) => ({
  requires_expiry: !!form.requiresExpiry,
  requires_issue_date: !!form.requiresIssueDate,
  requires_document_number: !!form.requiresDocumentNumber,
});

const buildCompanyTypeMetadata = (form) => {
  const meta = {};
  if (form.companyStructure) {
    meta.structure = form.companyStructure;
  }
  return meta;
};

const DocumentFieldChips = ({ row }) => {
  const rules = getDocumentTypeRules(row);
  const chips = [];
  if (rules.requiresExpiry) chips.push({ key: 'expiry', label: 'Expiry', color: 'warning' });
  if (rules.requiresIssueDate) chips.push({ key: 'issue', label: 'Issue date', color: 'info' });
  if (rules.requiresDocumentNumber) chips.push({ key: 'number', label: 'Doc number', color: 'primary' });

  if (!chips.length) {
    return (
      <Typography variant="body2" color="textSecondary">
        File only
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {chips.map((chip) => (
        <Chip key={chip.key} label={chip.label} size="small" color={chip.color} variant="outlined" />
      ))}
    </Stack>
  );
};

const DocumentFieldRulesEditor = ({ form, onChange }) => (
  <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 1, bgcolor: 'grey.50' }}>
    <Typography variant="subtitle2" gutterBottom>
      Upload field rules
    </Typography>
    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
      When users upload this document type, the selected fields become mandatory.
    </Typography>
    <FormControlLabel
      control={
        <Switch
          checked={form.requiresExpiry}
          onChange={(e) => onChange({ requiresExpiry: e.target.checked })}
        />
      }
      label="Require expiry date"
    />
    <FormControlLabel
      control={
        <Switch
          checked={form.requiresIssueDate}
          onChange={(e) => onChange({ requiresIssueDate: e.target.checked })}
        />
      }
      label="Require issue date"
    />
    <FormControlLabel
      control={
        <Switch
          checked={form.requiresDocumentNumber}
          onChange={(e) => onChange({ requiresDocumentNumber: e.target.checked })}
        />
      }
      label="Require document number"
    />
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
        Preview
      </Typography>
      <DocumentFieldChips row={{ metadata: buildDocumentMetadata(form) }} />
    </Box>
  </Paper>
);

const CompanyTypeStructureEditor = ({ form, onChange }) => (
  <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 1, bgcolor: 'grey.50' }}>
    <Typography variant="subtitle2" gutterBottom>
      Company structure classification
    </Typography>
    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
      Required for Create Company dropdown filtering. Select whether this type represents a parent/group company or subsidiary.
    </Typography>
    <FormControl fullWidth required>
      <InputLabel>Structure</InputLabel>
      <Select
        value={form.companyStructure}
        onChange={(e) => onChange({ companyStructure: e.target.value })}
        label="Structure"
      >
        <MenuItem value="">None (won't appear in Create Company)</MenuItem>
        <MenuItem value="GROUP">Parent / Group Company</MenuItem>
        <MenuItem value="SUBSIDIARY">Subsidiary</MenuItem>
      </Select>
    </FormControl>
  </Paper>
);

const LookupsAdmin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const isItAdmin = user?.role === 'IT_ADMIN';

  const [tab, setTab] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tablesMissing, setTablesMissing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCategory, setDialogCategory] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const categories = LOOKUP_CATEGORIES;
  const activeCategory = categories[tab]?.code;
  const showFieldRules = isDocumentTypeCategory(activeCategory);
  const dialogShowFieldRules = isDocumentTypeCategory(dialogCategory);
  const dialogShowStructure = isCompanyTypeCategory(dialogCategory);
  const dialogCategoryMeta = categories.find((c) => c.code === dialogCategory);

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogCategory(null);
    setEditing(null);
  };

  useEffect(() => {
    if (!isGroupScopeRole(user?.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const loadItems = async () => {
    try {
      setLoading(true);
      clearLookupCache();
      const result = await fetchLookups(activeCategory, { activeOnly: false });
      setItems(result.data || []);
      setTablesMissing(!!result.tablesMissing);
    } catch (err) {
      enqueueSnackbar('Failed to load lookups', { variant: 'error' });
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeCategory) loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
    [items]
  );

  const openCreate = () => {
    setEditing(null);
    setDialogCategory(activeCategory);
    setForm({
      ...emptyForm(),
      sortOrder: sortedItems.length + 1,
    });
    setDialogOpen(true);
  };

  const normalizeLookupCode = (category, code) => {
    const trimmed = String(code || '').trim();
    if (!trimmed) return '';
    if (isDocumentTypeCategory(category)) {
      return trimmed.toLowerCase().replace(/[\s-]+/g, '_');
    }
    return trimmed.toUpperCase();
  };

  const openEdit = (row) => {
    setEditing(row);
    setDialogCategory(row.category || activeCategory);
    setForm(formFromRow(row));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.label.trim()) {
      enqueueSnackbar('Label is required', { variant: 'warning' });
      return;
    }
    if (!editing && !form.code.trim()) {
      enqueueSnackbar('Code is required', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        const payload = {
          label: form.label.trim(),
          sortOrder: Number(form.sortOrder) || 0,
          isActive: form.isActive,
        };
        if (dialogShowFieldRules) {
          payload.metadata = buildDocumentMetadata(form);
        } else if (dialogShowStructure) {
          payload.metadata = buildCompanyTypeMetadata(form);
        }
        await csAdminApi.updateLookup(editing.id, payload);
        enqueueSnackbar('Lookup updated', { variant: 'success' });
      } else if (isItAdmin) {
        const payload = {
          category: dialogCategory || activeCategory,
          code: normalizeLookupCode(dialogCategory || activeCategory, form.code),
          label: form.label.trim(),
          sortOrder: Number(form.sortOrder) || 0,
        };
        if (dialogShowFieldRules) {
          payload.metadata = buildDocumentMetadata(form);
        } else if (dialogShowStructure) {
          payload.metadata = buildCompanyTypeMetadata(form);
        }
        await csAdminApi.createLookup(payload);
        enqueueSnackbar('Lookup created', { variant: 'success' });
      }
      closeDialog();
      clearLookupCache();
      await loadItems();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error?.message || 'Save failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const tableColSpan = (isItAdmin ? 1 : 0) + (showFieldRules ? 5 : 4);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Lookups
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Maintain dropdown values for company types, document types, countries, and more.
            {showFieldRules && ' Document types can require expiry, issue date, or document number on upload.'}
          </Typography>
        </Box>
        {isItAdmin && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add Value
          </Button>
        )}
      </Box>

      {tablesMissing && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Lookups table not found on the database. Built-in defaults are shown. Run{' '}
          <code>backend/scripts/csadmin/10-lookups.sql</code> (or <code>npm run csadmin:apply-ddl-rds</code>) to enable editing.
        </Alert>
      )}

      {!isItAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          View only. IT Admin can add or edit lookup values.
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          {categories.map((cat) => (
            <Tab key={cat.code} label={cat.label} />
          ))}
        </Tabs>
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {categories[tab]?.description}
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Label</TableCell>
                    {showFieldRules && <TableCell>Required fields</TableCell>}
                    <TableCell align="center">Sort</TableCell>
                    <TableCell align="center">Status</TableCell>
                    {isItAdmin && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedItems.map((row) => (
                    <TableRow key={`${row.category}-${row.code}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.code}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.label}</TableCell>
                      {showFieldRules && (
                        <TableCell>
                          <DocumentFieldChips row={row} />
                        </TableCell>
                      )}
                      <TableCell align="center">{row.sortOrder ?? 0}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={row.isActive !== false ? 'Active' : 'Inactive'}
                          size="small"
                          color={row.isActive !== false ? 'success' : 'default'}
                        />
                      </TableCell>
                      {isItAdmin && (
                        <TableCell align="right">
                          <Button size="small" onClick={() => openEdit(row)}>
                            Edit
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {!sortedItems.length && (
                    <TableRow>
                      <TableCell colSpan={tableColSpan} align="center">
                        <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                          No values in this category
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing
            ? `Edit ${dialogCategoryMeta?.label || 'Lookup Value'}`
            : `Add ${dialogCategoryMeta?.label || 'Lookup Value'}`}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {!editing && (
            <TextField
              fullWidth
              label="Code"
              value={form.code}
              onChange={(e) => {
                const value = isDocumentTypeCategory(dialogCategory)
                  ? e.target.value
                  : e.target.value.toUpperCase();
                setForm((p) => ({ ...p, code: value }));
              }}
              margin="normal"
              helperText={
                isDocumentTypeCategory(dialogCategory)
                  ? 'Lowercase snake_case (e.g. trade_license, emirates_id)'
                  : 'Short unique code (e.g. LS, FS, AUDIT)'
              }
            />
          )}
          <TextField
            fullWidth
            label="Label"
            value={form.label}
            onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
            margin="normal"
            required
          />
          {dialogShowFieldRules && (
            <DocumentFieldRulesEditor
              form={form}
              onChange={(patch) => setForm((p) => ({ ...p, ...patch }))}
            />
          )}
          {dialogShowStructure && (
            <CompanyTypeStructureEditor
              form={form}
              onChange={(patch) => setForm((p) => ({ ...p, ...patch }))}
            />
          )}
          <TextField
            fullWidth
            type="number"
            label="Sort order"
            value={form.sortOrder}
            onChange={(e) => setForm((p) => ({ ...p, sortOrder: e.target.value }))}
            margin="normal"
          />
          {editing && (
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                />
              }
              label="Active"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LookupsAdmin;
